/* =====================================================================
 * messageCache — IndexedDB-backed message cache (accountId-scoped)
 *
 * Provides instant message display on conversation click via
 * stale-while-revalidate: cached messages render immediately while
 * the backend fetch runs in the background.
 * ===================================================================== */

import { getActiveAccountId } from '../authVault';
import type { ChatMessage } from '@auw/workspace-utils';
import { activeTenantKey as tenantKey } from '@auw/tenant';

const DB_NAME = tenantKey('msg_cache');
const DB_VERSION = 1;
const STORE_NAME = 'messages';
const MAX_CACHED_CONVERSATIONS = 50;

interface CachedEntry {
  key: string;
  accountId: string;
  conversationId: string;
  messages: SerializableMessage[];
  cachedAt: number;
}

type SerializableMessage = Omit<ChatMessage, 'attachments'> & {
  attachments?: Array<Omit<ChatMessage['attachments'] extends (infer U)[] | undefined ? U : never, 'file'>>;
};

/* ------------------------------------------------------------------ */
/* DB lifecycle                                                        */
/* ------------------------------------------------------------------ */

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('accountId', 'accountId', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });

  return dbPromise;
}

/* ------------------------------------------------------------------ */
/* Serialization helpers                                               */
/* ------------------------------------------------------------------ */

function stripFile(msg: ChatMessage): SerializableMessage {
  if (!msg.attachments?.length) return msg as SerializableMessage;
  return {
    ...msg,
    attachments: msg.attachments.map(({ file: _file, ...rest }) => rest),
  };
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export async function getCachedMessages(conversationId: string): Promise<ChatMessage[] | null> {
  try {
    const accountId = getActiveAccountId();
    if (!accountId) return null;

    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const key = `${accountId}:${conversationId}`;

    return new Promise((resolve) => {
      const req = store.get(key);
      req.onsuccess = () => {
        const entry = req.result as CachedEntry | undefined;
        if (!entry || entry.accountId !== accountId) {
          resolve(null);
          return;
        }
        resolve(entry.messages as ChatMessage[]);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function setCachedMessages(
  conversationId: string,
  messages: ChatMessage[],
): Promise<void> {
  try {
    const accountId = getActiveAccountId();
    if (!accountId || messages.length === 0) return;

    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const entry: CachedEntry = {
      key: `${accountId}:${conversationId}`,
      accountId,
      conversationId,
      messages: messages.map(stripFile),
      cachedAt: Date.now(),
    };

    store.put(entry);
    evictOldEntries(db, accountId);
  } catch { /* IndexedDB failure is non-critical */ }
}

export async function clearMessageCache(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
  } catch { /* ignore */ }
}

/* ------------------------------------------------------------------ */
/* Eviction — keep at most MAX_CACHED_CONVERSATIONS per account        */
/* ------------------------------------------------------------------ */

function evictOldEntries(db: IDBDatabase, accountId: string): void {
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const idx = store.index('accountId');

    const countReq = idx.count(IDBKeyRange.only(accountId));
    countReq.onsuccess = () => {
      if (countReq.result <= MAX_CACHED_CONVERSATIONS) return;

      const toDelete = countReq.result - MAX_CACHED_CONVERSATIONS;
      const evictTx = db.transaction(STORE_NAME, 'readwrite');
      const evictStore = evictTx.objectStore(STORE_NAME);
      const cursor = evictStore.index('cachedAt').openCursor();
      let deleted = 0;

      cursor.onsuccess = () => {
        const c = cursor.result;
        if (!c || deleted >= toDelete) return;
        if ((c.value as CachedEntry).accountId === accountId) {
          c.delete();
          deleted++;
        }
        c.continue();
      };
    };
  } catch { /* best-effort eviction */ }
}
